import React, {useEffect, useState, useMemo} from 'react';
import ReactCSSTransitionReplace from 'react-css-transition-replace';
import {Props} from './ac-tab-container.proptypes';

// Imports => Utilities
import {AcIsSet, AcIsArray, AcSetHash, AcGetHash} from '@asdf-utils';

// Imports => Molecules
import {AcTabPanelContainer, AcTabPanel, AcTabBar} from '@asdf-molecules';
import {useHistory} from 'react-router-dom';

const AcTabContainer = ({
  collection = [],
  data = null,
  callback = () => {},
  initial = null,
  children,
  disableIndicator,
  selectForMobile,
  selectMinWidth,
  onlyDropdown = false,
  center = false,
  placeholder,
  forceShowOneItem = false,
  ...rest
}) => {
  const history = useHistory();
  const inCollection = hash => {
    if (!AcIsSet(hash)) {
      return;
    }

    let result = false;

    if (AcIsSet(collection) && AcIsArray(collection)) {
      result = collection.find(tab => tab.id === hash);
    }

    return result;
  };

  const getActiveTabID = () => {
    const hash = AcGetHash();

    let found = null;

    if (AcIsSet(hash) && inCollection(hash)) {
      found = hash;
    } else if (AcIsSet(initial) && inCollection(initial)) {
      found = initial;
    } else if (AcIsSet(collection) && AcIsArray(collection)) {
      if (AcIsSet(collection[0]) && AcIsSet(collection[0].id)) {
        found = collection[0].id;
      }
    }

    return found;
  };

  const [active, setActiveTab] = useState(getActiveTabID() || initial);

  useEffect(() => {
    findActiveTab();
  }, [history.location]);

  const findActiveTab = () => {
    const hash = getActiveTabID();

    if (AcIsSet(hash)) {
      setActiveTab(hash);
      handleTabChange(hash);
    }

    return hash;
  };

  const handleTabChange = id => {
    AcSetHash(id);
    setActiveTab(id);
    if (callback) {
      callback(id);
    }
  };

  const renderTabPanels = useMemo(() => {
    if (!AcIsSet(collection) || !AcIsArray(collection)) {
      return null;
    }

    const len = collection.length;
    let n = 0;
    let result = null;

    for (n; n < len; n++) {
      const item = collection[n];

      if (item.id === active) {
        const {id, component} = item;

        const opts = {
          key: `ac-tab-panel-${id}`,
          id,
          component,
          data,
        };

        const object = <AcTabPanel {...opts} />;

        result = object;
        break;
      }
    }

    return result;
  }, [collection, data, active]);

  const renderTabBar = useMemo(() => {
    if (!AcIsSet(collection) || !AcIsArray(collection)) {
      return null;
    }
    if (!AcIsSet(active)) {
      return null;
    }
    if (collection.length === 0 || (!forceShowOneItem && collection.length === 1)) {
      return null;
    }

    const len = collection.length;
    let n = 0;
    let result = [];

    for (n; n < len; n++) {
      const item = collection[n];

      result.push(item);
    }

    if (result.length === 0 || (!forceShowOneItem && result.length === 1)) {
      return null;
    }

    return (
      <AcTabBar
        tabs={result}
        active={active}
        callback={handleTabChange}
        disableIndicator={disableIndicator}
        selectForMobile={selectForMobile}
        selectMinWidth={selectMinWidth}
        onlyDropdown={onlyDropdown}
        center={center}
        placeholder={placeholder}
      >
        {children}
      </AcTabBar>
    );
  }, [children, collection, active]);

  return (
    <div className={'ac-tab-container-wrp'} {...rest}>
      <div className={'ac-tab-container'}>
        {renderTabBar}

        <div style={{width: '100%'}} key={`ac-tab-${active}`} className={'ac-tab-panels'}>
          {renderTabPanels}
        </div>
      </div>
    </div>
  );
};

AcTabContainer.displayName = 'AcTabContainer';
AcTabContainer.propTypes = Props;

export {AcTabContainer};
export default AcTabContainer;
